'use strict';

/**
 * Workday URL validation helpers.
 *
 * Valid Workday pod URL formats:
 *   https://wd3.myworkday.com/tenant          — standard pod
 *   https://wd3-impl.myworkday.com/tenant     — impl suffix variant
 *   https://impl.wd3.myworkday.com/tenant     — impl prefix variant
 *
 * Invalid (vanity domain, wrong host, etc.):
 *   https://www.myworkday.com/tenant
 *   https://acme.myworkday.com/tenant         (no wd<n> cluster)
 *   https://example.com/tenant
 *
 * Dual-mode file:
 *  - CommonJS export for Mocha unit tests (require'd directly)
 *  - Angular value 'workdayUrlValidator' registered on rocket.components for DI
 */

// Matches: https://wd<N>.myworkday.com/<tenant>
//      or: https://wd<N>-impl.myworkday.com/<tenant>
var POD_RE = /^https?:\/\/(wd\d+)(?:-impl)?\.myworkday\.com\/([^/?#]+)/i;

// Matches: https://impl.wd<N>.myworkday.com/<tenant>
var IMPL_PREFIX_RE = /^https?:\/\/impl\.(wd\d+)\.myworkday\.com\/([^/?#]+)/i;

/**
 * Parse a Workday URL and return { cluster, tenant } or null if not a valid pod URL.
 *
 * @param {string} url
 * @returns {{ cluster: string, tenant: string }|null}
 */
function parseWorkdayUrl(url) {
    if (!url) return null;

    var m = POD_RE.exec(url) || IMPL_PREFIX_RE.exec(url);
    if (!m) return null;

    return {
        cluster: m[1].toLowerCase(),
        tenant: m[2].replace(/\/$/, '').toLowerCase(),
    };
}

/**
 * Validate a Workday URL.
 *
 * Deliberately permissive: any well-formed http(s) URL is accepted verbatim.
 * Workday deployments are not limited to the myworkday.com pod domains — the
 * web-services hosts (wd3-impl-services1.workday.com), customer-specific
 * domains and versioned SOAP endpoints (/ccx/service/<tenant>/Recruiting/v46.2)
 * are all legal. Encoding a domain shape here rejected real, working tenants,
 * so the only thing checked now is that the value is a usable URL. The tenant
 * is supplied by its own field, and Workday itself is the authority on whether
 * the endpoint resolves — surfaced by the connection test.
 *
 * @param {string} url
 * @returns {string|null} Error message string if invalid, null if valid.
 */
function validateWorkdayUrl(url) {
    if (!url || !String(url).trim()) {
        return 'Workday URL is required';
    }

    var parsed;
    try {
        parsed = new URL(String(url).trim());
    } catch (e) {
        return 'Must be a valid URL including https:// (e.g. https://wd3.myworkday.com/your-tenant)';
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return 'Must be an http:// or https:// URL';
    }

    return null;
}

function validateWorkdaySourceFields(hostname, tenant) {
    if (!hostname || !String(hostname).trim()) return 'Workday hostname is required';
    if (!tenant || !String(tenant).trim()) return 'Workday tenant is required';
    return null;
}

/**
 * Cross-validate that the tenant parsed from the Workday URL matches the
 * @-suffix of the ISU username (username format: ISU_name@tenant).
 *
 * Only performs the check when both the URL parses successfully AND the
 * username contains an @-suffix.  Returns null (no error) in all other cases
 * so this does not produce false positives.
 *
 * @param {string} url
 * @param {string} username
 * @returns {string|null}
 */
function validateWorkdayTenantMatch(url, username) {
    var parsed = parseWorkdayUrl(url);
    if (!parsed) return null;                          // URL invalid — url validator handles that

    if (!username || username.indexOf('@') === -1) return null;  // no tenant suffix to compare

    var userTenant = username.split('@').pop().toLowerCase();
    if (userTenant !== parsed.tenant) {
        return 'Username tenant (@' + userTenant + ') does not match the tenant in the Workday URL (/' + parsed.tenant + ')';
    }
    return null;
}

// CommonJS export for Mocha unit tests
if (typeof module !== 'undefined' && typeof exports !== 'undefined' && module.exports === exports) {
    module.exports = {
        parseWorkdayUrl,
        validateWorkdaySourceFields,
        validateWorkdayUrl,
        validateWorkdayTenantMatch,
    };
}

// Angular DI registration — runs when loaded as a concatenated browser script.
// The IIFE guard prevents execution in Node/CommonJS environments (where
// `angular` is not defined).
if (typeof angular !== 'undefined') {
    angular.module('rocket.components').value('workdayUrlValidator', {
        parseWorkdayUrl: parseWorkdayUrl,
        validateWorkdaySourceFields: validateWorkdaySourceFields,
        validateWorkdayUrl: validateWorkdayUrl,
        validateWorkdayTenantMatch: validateWorkdayTenantMatch,
    });
}
